import { isoToPickerDate } from "@/shared/utils/date-formatting";
import { normalizeIndustrySector } from "../../domain/industry-sector";
function stripProtocol(value) {
    if (!value)
        return value;
    return value.replace(/^https?:\/\//i, "");
}
// OP-5494: socialLinks is a typed list of SocialProfileItemModel sourced from
// OPHUB social-profile endpoints. We coerce defensively because the field is
// declared `unknown` on the BFF contract until JSON polymorphism lands.
function parseSocialLinks(raw) {
    if (!Array.isArray(raw))
        return [];
    return raw
        .filter((item) => typeof item === "object" && item !== null)
        .map((item) => {
        const code = typeof item.networkCode === "string" ? item.networkCode : "";
        return {
            id: typeof item.id === "string" ? item.id : "",
            value: typeof item.value === "string" ? item.value : "",
            networkId: typeof item.networkId === "string" ? item.networkId : "",
            networkCode: code,
            networkName: typeof item.networkName === "string" ? item.networkName : code,
            urlPrefix: typeof item.urlPrefix === "string" ? item.urlPrefix : null,
            iconClass: typeof item.iconClass === "string" ? item.iconClass : "",
            label: typeof item.label === "string" ? item.label : null,
        };
    });
}
function getHardcodedValues(layout, blockId) {
    const block = layout.layout.blocks.find((b) => b.id === blockId);
    return block?.kind === "hardcoded" ? block.values : {};
}
function mapProperties(props) {
    if (!props) {
        return {
            externalId: null,
            status: null,
            archivingReason: null,
            geolocalisation: null,
            createdAt: null,
            updatedAt: null,
            createdBy: null,
            updatedBy: null,
        };
    }
    return {
        externalId: props.externalId,
        status: props.status,
        archivingReason: props.archivingReason,
        geolocalisation: props.geolocalisation,
        createdAt: props.createdAt,
        updatedAt: props.updatedAt,
        createdBy: props.createdBy ? { id: props.createdBy.id, displayName: props.createdBy.displayName } : null,
        updatedBy: props.updatedBy ? { id: props.updatedBy.id, displayName: props.updatedBy.displayName } : null,
    };
}
/**
 * `Organisation.properties` is optional on the entity (not every construction site has
 * audit metadata), but this mapper always populates it: mapProperties returns a fully
 * null-filled object when the BFF omits the block. The narrowed return type lets callers
 * read `properties` without a null guard.
 */
export function mapLayoutToOrganisation(layout) {
    const main = getHardcodedValues(layout, "main-info");
    const admin = getHardcodedValues(layout, "admin-info");
    const contact = getHardcodedValues(layout, "communication");
    const rawPhones = [...(contact.phones ?? [])].sort((a, b) => {
        const aP = a.isPrimary ? 1 : 0;
        const bP = b.isPrimary ? 1 : 0;
        return bP - aP;
    });
    const phones = rawPhones.map((p) => {
        if (typeof p === "string")
            return { id: "", label: "", countryCode: "", dialCode: "", number: p, isPrimary: false };
        const obj = p;
        if (typeof obj.value === "string")
            return {
                id: typeof obj.id === "string" ? obj.id : "",
                label: "",
                countryCode: "",
                dialCode: "",
                number: obj.value,
                isPrimary: obj.isPrimary === true,
                subType: obj.subType === "Mobile" || obj.subType === "Fixe" ? obj.subType : undefined,
            };
        return p;
    });
    const rawEmails = [...(contact.emails ?? [])].sort((a, b) => {
        const aP = a.isPrimary ? 1 : 0;
        const bP = b.isPrimary ? 1 : 0;
        return bP - aP;
    });
    const emails = rawEmails.map((e) => {
        if (typeof e === "string")
            return { id: "", label: "", address: e, isPrimary: false };
        const obj = e;
        if (typeof obj.value === "string")
            return {
                id: typeof obj.id === "string" ? obj.id : "",
                label: "",
                address: obj.value,
                isPrimary: obj.isPrimary === true,
            };
        return e;
    });
    const properties = mapProperties(layout.properties);
    return {
        id: layout.id,
        externalId: properties.externalId ?? "",
        avatarUrl: typeof main.logo === "string" ? main.logo : null,
        status: properties.status ?? "",
        createdAt: properties.createdAt ?? "",
        updatedAt: properties.updatedAt ?? "",
        tags: [],
        addresses: [],
        mainInformation: {
            legalName: String(main.legalName ?? ""),
            acronym: String(main.acronym ?? ""),
            tradeName: main.tradeName ?? null,
            organisationType: String(main.organisationType ?? ""),
            relationTypes: Array.isArray(main.relationTypes)
                ? main.relationTypes.filter((v) => typeof v === "string")
                : [],
            industrySector: normalizeIndustrySector(typeof main.industrySector === "string" ? main.industrySector : null),
            description: String(main.description ?? ""),
        },
        administrativeInformation: {
            legalForm: String(admin.legalForm ?? ""),
            establishmentNature: String(admin.establishmentNature ?? ""),
            registrationDate: isoToPickerDate(admin.registrationDate),
            headcount: admin.headcount ?? null,
            employeeRange: admin.employeeRange ?? null,
            employeeRangeYear: admin.employeeRangeYear ?? null,
            siret: String(admin.siret ?? ""),
            siretValidation: null,
            activityCode: String(admin.activityCode ?? ""),
            activityCodeLabel: "",
            vatNumber: String(admin.vatNumber ?? ""),
            vatValidation: null,
            turnover: admin.turnover ?? null,
            legalStatus: admin.legalStatus ?? null,
            parentOrganisationId: admin.parentOrganisationId ?? null,
            parentOrganisationName: admin.parentOrganisationName ?? null,
        },
        contactInformation: {
            phones,
            emails,
            domainName: stripProtocol(contact.domainName),
            socialLinks: parseSocialLinks(contact.socialLinks),
        },
        properties,
    };
}
